import Vue from 'vue'
import Router from 'vue-router'
import { ability } from '@/helpers/abilities'
import { getItem } from '@/helpers/storage-manager'

// modules
import auth from '@/store/modules/auth'
import store from '@/store/store'

import { getDashboardTitle, getDashboardType } from '@/helpers/dashboard-name-helper'
import {getRefreshToken} from './helpers/request'

Vue.use(Router)

const router = new Router({
  mode: 'history',
  base: process.env.BASE_URL,
  scrollBehavior() {
    return { x: 0, y: 0 }
  },
  routes: [
    { path: '/index.html', redirect: '/' },
    {
      path: '/design/components',
      component: () => import('@/views/design/Components.vue')
    },
    {
      path: '/design/components/inbox-preview',
      component: () => import('@/views/design/InboxPreview.vue')
    },
    {
      path: '/callback',
      name: 'Callback'
    },
    {
      path: '/google-calendars/callback',
      name: 'GoogleapisCallback',
      component: () => import('@/views/common/GoogleCalendarCallback.vue')
    },
    {
      path: '/politica-privacidad-mit',
      name: 'Política Privacidad MIT',
      component: () => import('@/views/main/PoliticaPrivacidadMIT.vue')
    },
    {
      path: '/politica-privacidad',
      name: 'Política Privacidad',
      component: () => import('@/views/main/PoliticaPrivacidad.vue')
    },
    {
      path: '/terminos-condiciones',
      name: 'Términos Condiciones',
      component: () => import('@/views/main/TerminosCondiciones.vue')
    },
    {
      path: '/',
      name: 'root',
      beforeEnter: (to, from, next) => {
        try {
          if (auth.getters.isAuthenticated() && Vue.prototype.$getCookie('user')) {
            return next({ name: 'dashboard', query: to.query, params: to.params })
          }
          return next({ name: 'login', query: to.query, params: to.params })
        } catch (e) {
          console.error(e)
          return next({ name: 'login' })
        }
      },
    },
    // MAIN SECTIONS
    // AUTHENTICATION
    {
      path: '/login',
      name: 'login',
      component: () => import('@/views/authentication/Login.vue'),
      beforeEnter: (to, from, next) => {
        if (auth.getters.isAuthenticated() && Vue.prototype.$getCookie('user')) {
          next({ name: 'dashboard' })
        } else {
          next()
        }
      }
    },
    {
      path: '/recover-password',
      name: 'recover-password',
      component: () => import('@/views/authentication/RecoverPassword.vue')
    },
    {
      path: '/reset-password/:token',
      name: 'reset-password',
      component: () => import('@/views/authentication/ResetPassword.vue')
    },
    {
      path: '/not-found',
      name: 'NotFound',
      component: () => import('@/views/authentication/404.vue')
    },
    {
      path: '/no-access',
      name: 'NoAccess',
      component: () => import('@/views/authentication/403.vue')
    },
    {
      path: '/landing',
      name: 'Landing',
      component: () => import('@/views/main/Landing.vue')
    },
    {
      path: '/payment-subscription/:account_id',
      name:'PaymentSubscription',
      component: () => import('@/views/account/PaymentSubscription.vue')
    },
    {
      path: '/logout',
      name: 'logout',
      beforeEnter: async (to, from, next) => {
        try {
          await store.dispatch('auth/logout')
          store.commit('organization/setPlugins', { success: false })

          if (auth.getters.isAuthenticated()) {
            try {
              await store.dispatch('admin/disconnectAdmin')
              await store.dispatch('admin/disconnectAccountAdmin')
              await Vue.prototype.$auth.logout()
            } catch (e) {
              console.log(e)
            }
          }

          next({ name: 'root', query: to.query, params: to.params })
        } catch (e) {
          console.log(e)
        }
      }
    },
    {
      path: '/payment_result',
      name: 'payment result',
      component: () => import('@/views/main/PaymentResult.vue'),
      meta: {
        requiresAuth: true
      }
    },
    // MAIN
    {
      path: '/dashboard',
      name: 'dashboard',
      component: () => import('./views/main/Dashboard.vue'),
      meta: {
        requiresAuth: true
      },
      beforeEnter: (to, from, next) => {
        if (getCurrentUser().partner_id) {
          next({ name: 'partner', params: { partner_id: getCurrentUser().partner_id } })
        } else {
          next()
        }
      },
      redirect: { name: 'organizations' },
      children: [
        {
          path: 'organizations',
          name: 'organizations',
          component: () => import('@/views/main/Organizations.vue'),
          beforeEnter: (to, from, next) => {
            if (!ability.can('read', 'organization')) {
              next({ name: 'accounts' })
            } else {
              next()
            }
          }
        },
        {
          path: 'accounts',
          name: 'accounts',
          component: () => import('@/views/main/Accounts.vue'),
        },
        // {
        //   path: 'subscriptions',
        //   name: 'subscriptions',
        //   meta: {
        //     resource: 'subscription-view'
        //   },
        //   component: () => import('./views/main/Subscriptions-inactive.vue')
        // },
        {
          path: 'admin_user',
          name: 'admin_user',
          component: () => import('@/views/main/Admin_User.vue')
        },
        {
          path: '/auth/shopify/account',
          name: 'Auth Shopify Account',
          component: () => import('@/views/main/Shopify.vue'),
          meta: {
            requiresAuth: true
          }
        },
      ]
    },
    // ORGANIZATION
    {
      path: '/organization/:organization_id',
      name: 'organization',
      component: () => import('./views/organization/Dashboard.vue'),
      redirect: { name: 'organization-accounts' },
      meta: {
        requiresAuth: true
      },
      beforeEnter: (to, from, next) => {
        if (isNaN(to.params.organization_id)) {
          next({ name: 'root' })
        } else {
          next()
        }
      },
      children: [
        {
          path: 'accounts',
          name: 'organization-accounts',
          component: () => import('@/views/organization/Accounts.vue')
        },
        {
          path: 'admins',
          name: 'admins',
          meta: {
            resource: 'admin-view'
          },
          component: () => import('@/views/organization/Admins.vue')
        },
        {
          path: 'organization-subscriptions',
          name: 'organization-subscriptions',
          meta: {
            resource: 'subscription-view'
          },
          component: () => import('./views/organization/Subscriptions.vue')
        }
      ]
    },
    // ACCOUNT
    {
      path: '/stripe/account/:account_id/refresh',
      name: 'Stripe Refresh',
      meta: {
        requiresAuth: true,
        resource: 'connect-stripe'
      },
      component: () => import('@/views/account/RefreshStripeLink.vue'),
    },
    {
      path: '/paypal-callback',
      name: 'Paypal Callback',
      meta: {
        requiresAuth: true,
        resource: 'connect-paypal'
      },
      component: () => import('@/views/account/PaypalCallback.vue'),
    },
    {
      path: '/account/:account_id',
      name: 'account',
      component: () => import('./views/account/Dashboard.vue'),
      meta: {
        requiresAuth: true,
        name: 'Cuenta'
      },
      redirect: (to) => {
        if (isNaN(to.params.account_id)) {
          return { name: 'root' }
        }

        return { name: 'PluginsLoader' }
      },
      beforeEnter: async (to, from, next) => {
        const account = await getAccount(to.params.account_id)

        if (!account) return next({ name: 'root' })
        if (!account.is_active) {
          return next(
            {
              name: 'PaymentSubscription',
              params: { account_id: to.params.account_id }
            }
          )
        }

        return next()
      },
      children: [
        {
          path: 'plugins-loader',
          name: 'PluginsLoader',
          meta: {
            name: 'Cargando plugins'
          },
          component: () => import('@/views/account/PluginsLoader.vue'),
        },
        {
          path: 'inbox',
          name: 'Inbox',
          meta: {
            resource: 'inbox-view',
            name: 'Inbox'
          },
          redirect: () => {
            if (ability.can('read', 'case')) {
              return { name: 'Cases' }
            } else {
              return { name: 'Chats' }
            }
            /*if (ability.can('read', 'case')) {
              if (ability.can('read', 'case-group')) {
                return { name: 'Cases' }
              } else {
                return { name: 'Assigned' }
              }
            } else {
              return { name: 'Chats' }
            }*/
          },
          component: () => import('./views/account/Inbox.vue'),
          children: [
            {
              path: 'cases',
              name: 'Cases',
              meta: {
                resource: 'case',
                name: 'Casos'
              },
              component: () => import('./views/account/Cases.vue'),
              /*beforeEnter(to, from, next) {
                try {
                  if (to.name === 'Cases') {
                    if (ability.can('read', 'case-group')) {
                      next()
                    } else {
                      next({ name: 'Assigned', params: to.params })
                    }
                  } else {
                    next()
                  }
                } catch (e) {}
              },*/
              children: [
                /*{
                  path: 'group/:group_id',
                  name: 'Group',
                  meta: {
                    resource: 'case-group',
                    name: 'Grupo'
                  },
                  component: () => import('@/components/GroupList.vue'),
                  beforeEnter: (to, from, next) => {
                    if (isNaN(to.params.group_id)) {
                      next({ name: 'Cases', params: to.params })
                    } else {
                      next()
                    }
                  },
                  children: [
                    {
                      path: 'case/:case_id',
                      name: 'Group Case',
                      meta: {
                        resource: 'case',
                        name: 'Caso'
                      },
                      component: () => import('@/views/account/Case.vue'),
                      beforeEnter: (to, from, next) => {
                        if (isNaN(to.params.case_id)) {
                          next({ name: 'Cases', params: to.params })
                        } else {
                          next()
                        }
                      }
                    },
                  ]
                },
                {
                  path: 'assigned',
                  name: 'Assigned',
                  meta: { name: 'Asignados' },
                  component: () => import('@/components/GroupList.vue'),
                  children: [
                    {
                      path: 'case/:case_id',
                      name: 'Assigned Case',
                      meta: {
                        resource: 'case',
                        name: 'Caso'
                      },
                      component: () => import('@/views/account/Case.vue'),
                      beforeEnter: (to, from, next) => {
                        if (isNaN(to.params.case_id)) {
                          next({ name: 'Cases', params: to.params })
                        } else {
                          next()
                        }
                      }
                    },
                  ]
                },*/
                {
                  path: ':case_id',
                  name: 'Case',
                  meta: {
                    resource: 'case',
                    name: 'Caso'
                  },
                  component: () => import('./views/account/Case.vue'),
                  beforeEnter: (to, from, next) => {
                    if (isNaN(to.params.case_id)) {
                      next({ name: 'Cases', params: to.params })
                    } else {
                      next()
                    }
                  }
                },
              ]
            },
            /*{
              path: 'cases/:case_id',
              name: 'Case',
              meta: {
                resource: 'case',
                name: 'Caso'
              },
              component: () => import('@/views/account/Case.vue'),
              beforeEnter: (to, from, next) => {
                if (isNaN(to.params.case_id)) {
                  next({ name: 'Cases', params: to.params })
                } else {
                  next()
                }
              }
            },*/
            {
              path: 'chats',
              name: 'Chats',
              meta: {
                resource: 'chat-view',
                name: 'Conversaciones'
              },
              component: () => import('./views/account/Chats.vue'),
              children: [
                {
                  path: ':conversation_id',
                  name: 'Conversation',
                  meta: {
                    resource: 'chat-view',
                    name: 'Conversación'
                  },
                  component: () => import('@/components/Composer.vue')
                }
              ]
            },
            {
              path: 'resources',
              name: 'Resources',
              component: () => import('@/views/account/Resources.vue'),
              meta: {
                resource: 'inbox-resources',
                name: 'Recursos'
              },
              redirect: { name: 'Groups' },
              children: [
                {
                  path: 'groups',
                  name: 'Groups',
                  meta: {
                    resource: 'inbox-resources',
                    name: 'Grupos'
                  },
                  component: () => import('@/views/account/Groups.vue')
                },
                {
                  path: 'tags',
                  name: 'Tags',
                  component: () => import('@/views/account/Tags.vue'),
                  meta: {
                    resource: 'inbox-resources',
                    name: 'Etiquetas'
                  }
                },
                {
                  path: 'responses',
                  name: 'Responses',
                  meta: {
                    resource: 'inbox-resources',
                    name: 'Respuestas'
                  },
                  component: () => import('@/views/account/Responses.vue')
                },
                {
                  path: 'keywords',
                  name: 'Keywords',
                  meta: {
                    resource: 'inbox-resources',
                    name: 'Palabras clave'
                  },
                  component: () => import('@/views/account/Keywords.vue')
                },
                {
                  path: 'agents',
                  name: 'Agents',
                  meta: {
                    resource: 'admin-view',
                    name: 'Agentes'
                  },
                  component: () => import('@/views/account/Agents.vue')
                }
              ]
            }
          ]
        },
        {
          path: 'campaigns',
          name: 'Broadcast',
          meta: {
            resource: 'broadcast-view',
            name: 'Campañas'
          },
          redirect: { name: 'Campaigns' },
          component: () => import('@/views/account/CampaignsRouter.vue'),
          children: [
            {
              path: '',
              name: 'Campaigns',
              component: () => import('@/views/account/Campaigns.vue'),
              meta: { name: 'Inicio' },
            },
            {
              path: 'campaigns-results',
              name: 'Campaigns Results',
              meta: { name: 'Resultados de campaña' },
              component: () => import('@/views/account/CampaignsResults.vue'),
            },
            {
              path: 'whatsapp-templates',
              name: 'Whatsapp Templates',
              meta: { name: 'Plantillas de Whatsapp' },
              component: () => import('@/views/account/WhatsappTemplates.vue'),
            },
            {
              path: ':wabaId?',
              name: 'New Whatsapp Template',
              meta: { name: 'Nueva Plantillas de Whatsapp' },
              component: () => import('@/views/account/WhatsappNewTemplate.vue'),
            },
          ]
        },
        {
          path: 'broadcast',
          name: 'Broadcast OLD',
          meta: {
            resource: 'broadcast-view',
          },
          component: () => import('@/views/account/Broadcast.vue'),
        },
        {
          path: 'message-templates',
          name: 'Message Templates',
          meta: {
            name: 'Plantillas de mensajes'
          },
          component: () => import('@/views/account/MessageTemplates.vue'),
        },
        {
          path: 'flows',
          meta: {
            resource: 'flow-view',
            name: 'Flujos'
          },
          component: () => import('@/views/account/Flows.vue'),
          name: 'Flow',
          redirect: { name: 'Flows list' },
          children: [
            {
              path: '',
              component: () => import('@/views/account/FlowsList.vue'),
              name: 'Flows list',
              meta: {
                name: 'Lista de flujos'
              }
            },
            {
              path: ':flow_id',
              name: 'Flow editor',
              meta: {
                name: 'Editor de flujo'
              },
              component: () => import('./views/account/Flow.vue'),
              children: [
                {
                  path: 'block/:block_id',
                  component: () => import('./components/BlockEditor.vue'),
                  name: 'Block',
                  meta: {
                    name: 'Bloque'
                  }
                },
              ]
            },
            {
              path: 'builder/:flow_id',
              name: 'Flow builder',
              meta: {
                // resource: 'flow-builder-view',
                name: 'Mapa de flujo'
              },
              component: () => import('./views/account/FlowBuilder.vue')
            },
          ]
        },
        {
          path: 'kpis',
          name: 'KPI',
          meta: {
            resource: 'kpi-view',
            name: 'KPIs'
          },
          component: () => import('@/views/account/Kpis.vue'),
        },
        {
          path: 'publisher',
          name: 'Publisher',
          component: () => import('@/views/account/Publisher.vue'),
          meta: {
            resource: 'publisher-view',
            name: 'Publicar'
          },
          redirect: { name: 'Facebook' },
          children: [
            {
              path: 'facebook',
              name: 'Facebook',
              meta: {
                resource: 'publisher-view',
                name: 'Facebook'
              },
              component: () => import('./views/account/public_media/facebook.vue')
            },
            {
              path: 'instagram',
              name: 'Instagram',
              meta: {
                resource: 'publisher-view',
                name: 'Instagram'
              },
              component: () => import('./views/account/public_media/instagram.vue')
            },
          ]
        },
        {
          path: 'sequences',
          name: 'Sequences',
          meta: {
            resource: 'sequence',
            name: 'Secuencias'
          },
          component: () => import('@/views/account/Sequences.vue'),
          children: [
            {
              path: ':sequence_id',
              name: 'Sequence',
              component: () => import('@/components/SequenceBlock.vue'),
            }
          ]
        },
        {
          path: 'rules',
          name: 'Rule',
          meta: {
            resource: 'rule',
            name: 'Reglas'
          },
          component: () => import('@/views/account/Rules.vue'),
        },
        {
          path: 'extensions',
          name: 'Extension',
          meta: {
            resource: 'extension-view',
            name: 'Extensiones'
          },
          component: () => import('@/views/account/Lambdas.vue'),
        },
        {
          path: 'extensions/:lambda_id',
          name: 'Editor',
          meta: {
            resource: 'extension-view',
            name: 'Editor'
          },
          component: () => import('@/views/account/Lambda.vue')
        },
        {
          path: 'entities',
          name: 'Entities',
          meta: {
            resource: 'entity-view',
            name: 'Entidades'
          },
          component: () => import('@/views/account/Entities.vue'),
          children: [
            {
              path: ':entity_id',
              name: 'Entity',
              component: () => import('@/components/Entity.vue'),
            }
          ]
        },
        {
          path: 'ecommerce',
          name: 'Ecommerce',
          meta: {
            name: 'Ecommerce'
          },
          component: () => import('@/views/account/Ecommerce.vue'),
          redirect: { name: 'Products' },
          children: [
            {
              path: 'services',
              meta: {
                resource: 'product',
                name: 'Servicios'
              },
              component: () => import('@/views/common/simpleRouter.vue'),
              children: [
                {
                  path: '',
                  name: 'Services',
                  meta: {
                    name: 'Servicios'
                  },
                  component: () => import('@/views/account/Services.vue')
                },
                {
                  path: ':product_id',
                  name: 'Service',
                  meta: {
                    name: 'Servicio'
                  },
                  beforeEnter: (to, from, next) => {
                    if (isNaN(to.params.product_id) && to.params.product_id !== 'new') {
                      return next({ name: 'Services', params: to.params })
                    }

                    next()
                  },
                  component: () => import('./views/account/Product.vue')
                }
              ]
            },
            {
              path: 'home',
              name: 'Home',
              meta: {
                name: 'Inicio'
              },
              component: () => import('@/views/account/EcommerceHome.vue')
            },
            {
              path: 'products-categories',
              component: () => import('./views/common/simpleRouter.vue'),
              redirect: { name: 'Products Categories' },
              children: [
                {
                  path: 'list',
                  name: 'Products Categories',
                  meta: {
                    resource: 'category',
                    name: 'Categorías'
                  },
                  component: () => import('@/views/account/ProductsCategories.vue')
                },
                {
                  path: ':category_id',
                  name: 'Category',
                  meta: {
                    resource: 'category',
                    name: 'Categoría'
                  },
                  component: () => import('@/views/account/Category.vue')
                },
              ]
            },
            {
              path: 'products',
              name: 'Products',
              redirect: { name: 'Categories' },
              meta: {
                resource: 'product',
                name: 'Productos'
              },
              component: () => import('./views/common/simpleRouter.vue'),
              children: [
                {
                  path: 'categories',
                  name: 'Categories',
                  meta: {
                    name: 'Categorías'
                  },
                  component: () => import('./views/account/Products_v2.vue'),
                  children: [
                    {
                      path: ':category_id',
                      name: 'ProductsList',
                      meta: {
                        resource: 'product',
                        name: 'Lista de productos'
                      },
                      component: () => import('./views/account/ProductsList.vue')
                    }
                  ]
                },
                {
                  path: ':product_id',
                  name: 'Product',
                  meta: {
                    name: 'Producto'
                  },
                  beforeEnter: (to, from, next) => {
                    if (isNaN(to.params.product_id) && to.params.product_id !== 'new') {
                      return next({ name: 'Categories', params: to.params })
                    }

                    next()
                  },
                  component: () => import('./views/account/Product.vue')
                }
              ],
            },
            {
              path: 'products-package',
              name: 'Products Package',
              meta: { name: 'Paquetes de productos' },
              component: () => import('@/views/account/ProductsPackage.vue'),
              children: [
                {
                  path: ':product_package_id',
                  name: 'Package',
                  meta: { name: 'Paquete' },
                  component: () => import('@/views/account/Package.vue')
                }
              ]
            }
          ]
        },
        {
          path: 'orders',
          name: 'Orders',
          meta: {
            resource: 'order',
            name: 'Órdenes'
          },
          component: () => import('@/views/account/Orders.vue'),
        },
        {
          path: 'reach',
          name: 'Dashboard',
          meta: {
            resource: 'dashboard-view',
            name: 'Alcance'
          },
          component: () => import('@/views/account/Reach.vue'),
        },
        {
          path: 'users',
          name: 'User',
          meta: {
            resource: 'user-view',
            name: 'Usuarios'
          },
          component: () => import('@/views/account/Users.vue'),
        },
        {
          path: 'delivery',
          name: 'Delivery',
          component: () => import('@/views/account/Delivery.vue'),
          meta: {
            resource: 'delivery',
            name: 'Recolección y envíos'
          },
          redirect: { name: 'Shipping' },
          children: [
            {
              path: 'shipping',
              name: 'Shipping',
              meta: {
                name: 'Envíos'
              },
              component: () => import('@/views/account/Shipping.vue'),
              // beforeEnter: async (to, from, next) => {
              //   let delivery_type = await getEcommerce(to.params.account_id)
              //   switch (delivery_type) {
              //     case 1:
              //       next()
              //       break
              //     case 2:
              //       next({ name: 'Recolection' })
              //       break
              //     default:
              //       next({ name: 'root' })
              //       break
              //   }
              // },
            },
            {
              path: 'recolection',
              name: 'Recolection',
              meta: {
                name: 'Recolección'
              },
              component: () => import('@/views/account/Kanguro.vue'),
            },
          ]
        },
        {
          path: 'variables',
          name: 'Variable',
          component: () => import('@/views/account/Variables.vue'),
          meta: {
            resource: 'variable-view',
            name: 'Variables'
          },
          redirect: { name: 'Account' },
          children: [
            {
              path: 'account',
              name: 'Account',
              meta: {
                resource: 'variable-view',
                name: 'Cuenta'
              },
              component: () => import('./views/account/VariablesAccount.vue')
            },
            {
              path: 'global',
              name: 'Global',
              meta: {
                resource: 'variable-view',
                name: 'Global'
              },
              component: () => import('./views/account/VariablesGlobal.vue')
            },
          ]
        },
        {
          path: 'metrics',
          name: 'Metric',
          redirect: { name: 'Metrics' },
          component: () => import('./views/account/MetricsRouter'),
          children: [
            {
              path: '',
              name: 'Metrics',
              component: () => import('./views/account/Metrics.vue'),
              meta: {
                resource: 'metric-view',
                name: 'Métricas'
              }
            },
            {
              path: ':metric_id',
              name: 'Metric Detail',
              meta: {
                resource: 'metric-view',
                name: 'Métrica'
              },
              component: () => import('./views/account/Metric.vue'),
              /*redirect: { name: 'Metric Attributes' },
              children: [
                {
                  path: 'graphs',
                  name: 'Metric Graphs',
                  meta: {
                    name: 'Gráficas'
                  },
                  component: () => import('./views/account/MetricGraph.vue'),
                },
                {
                  path: 'attributes',
                  name: 'Metric Attributes',
                  meta: {
                    name: 'Atributos'
                  },
                  component: () => import('./views/account/MetricAttributes.vue'),
                },
                {
                  path: 'records',
                  name: 'Metric Records',
                  meta: {
                    name: 'Registros'
                  },
                  component: () => import('./views/account/MetricRecords.vue'),
                },
              ]*/
            },
          ]
        },
        {
          path: 'customization',
          name: 'Customization',
          meta: {
            resource: 'customization-view',
            name: 'Personalización'
          },
          component: () => import('@/views/account/Customization.vue'),
        },
        {
          path: 'settings',
          name: 'Settings',
          meta: {
            resource: 'settings-view',
            name: 'Configuración'
          },
          component: () => import('@/views/account/Configuration.vue'),
          redirect: () => {
            if (ability.can('update', 'settings-platforms-view')) {
              return { name: 'Platforms' }
            } else if (ability.can('update', 'settings-ecommerce-view')) {
              return { name: 'Ecommerce Settings' }
            } else if (ability.can('update', 'parameter')) {
              return { name: 'Parameters' }
            } else if (ability.can('update', 'account-configuration')) {
              return { name: 'Account Configuration' }
            } else if (ability.can('update', 'account-configuration')) {
              return { name: 'Advanced' }
            } else {
              return {name: 'my_account'}
            }
          },
          children: [
            {
              path: 'platforms',
              name: 'Platforms',
              component: () => import('@/views/common/Platforms.vue'),
              meta: {
                resource: 'settings-platforms-view',
                name: 'Plataformas'
              },
            },
            {
              path: 'facebook-shops',
              name: 'Facebook Shops',
              component: () => import('@/views/common/FacebookShops.vue'),
              meta: {
                resource: 'settings-platforms-view',
                name: 'Shops'
              },
            },
            {
              path: 'ecommerce',
              name: 'Ecommerce Settings',
              component: () => import('@/views/common/Ecommerce.vue'),
              meta: {
                resource: 'settings-ecommerce-view',
                name: 'Configuración de Ecommerce'
              },
            },
            {
              path: 'parameters',
              name: 'Parameters',
              component: () => import('@/views/common/Parameters.vue'),
              meta: {
                resource: 'parameter',
                name: 'Parámetros'
              },
            },
            {
              path: 'account_configuration',
              name: 'Account Configuration',
              component: () => import('@/views/common/AccountConfiguration.vue'),
              meta: {
                resource: 'account-configuration',
                name: 'Configuración de Cuenta'
              },
            },
            {
              path: 'ia_configuration',
              name: 'IA Configuration',
              component: () => import('@/views/common/IAConfiguration.vue'),
              meta: {
                resource: 'account-configuration',
                name: 'Configuración de IA'
              },
            },
            {
              path: 'advanced',
              name: 'Advanced',
              meta: {
                resource: 'account-configuration',
                name: 'Avanzado'
              },
              component: () => import('@/views/common/Advanced.vue')
            },
            {
              path: 'google-calendars',
              name: 'GoogleCalendars',
              meta: {
                resource: 'account-configuration',
                name: 'Calendarios Google'
              },
              component: () => import('@/views/common/GoogleCalendars.vue')
            },
            {
              path: 'shop',
              name: 'Shop',
              meta: {
                name: 'Konverza Shop'
              },
              component: () => import('@/views/account/Shop.vue')
            },
            {
              path: 'my_account',
              name: 'my_account',
              component: () => import('@/views/main/Admin_User.vue')
            },
          ]
        },
        {
          path: 'my-account',
          name: 'My Account',
          meta: {
            name: 'Mi Cuenta'
          },
          component: () => import('@/views/account/MyAccount.vue'),
          redirect: {name: 'Affiliation'},
          children: [
            {
              path: 'affiliation',
              name: 'Affiliation',
              meta: {
                resource: 'affiliation-view',
                name: 'Afiliación'
              },
              component: () => import('@/views/account/Affiliation.vue'),
            },
            {
              path: 'stripe',
              name: 'Stripe',
              meta: {
                resource: 'connect-stripe',
                name: 'Stripe'
              },
              component: () => import('@/views/account/Stripe.vue'),
            },
            {
              path: 'paypal',
              name: 'Paypal',
              meta: {
                resource: 'connect-paypal',
                name: 'PayPal'
              },
              component: () => import('@/views/account/Paypal.vue'),
            },
            {
              path: 'billing',
              name: 'Billing',
              redirect: { name: 'Active Plan' },
              component: () => import('@/views/account/Billing.vue'),
              meta: {
                resource: 'billing-view',
                name: 'Cuenta de Cobros'
              },
              children: [
                {
                  path: 'active-plan',
                  name: 'Active Plan',
                  meta: {
                    name: 'Plan Activo'
                  },
                  component: () => import('./views/account/ActivePlan.vue'),
                },
                {
                  path: 'account-history',
                  name: 'History',
                  meta: {
                    name: 'Historial'
                  },
                  component: () => import('./views/account/AccountHistory.vue'),
                },
                {
                  path: 'account-cards',
                  name: 'Cards',
                  meta: {
                    name: 'Tarjetas guardadas'
                  },
                  component: () => import('./views/account/AccountCards.vue'),
                },
                {
                  path: 'plans',
                  name: 'Plans',
                  meta: {
                    name: 'Planes'
                  },
                  component: () => import('@/components/Plans.vue')
                }
              ]
            },
            /*{
              path: 'reports',
              name: 'Report',
              meta: {
                resource: 'report-view',
                name: 'Reportes'
              },
              component: () => import('@/views/account/Reports.vue'),
            },*/
            /*{
              path: 'account-agents',
              name: 'Account agents',
              meta: {
                name: 'Agentes de cuenta'
              },
              redirect: { name: 'Accounts agents' },
              component: () => import('@/views/account/AdminRouter.vue'),
              children: [
                {
                  path: '',
                  name: 'Accounts agents',
                  component: () => import('@/views/account/Admins.vue'),
                  meta: {
                    resource: 'admin-view',
                    name: 'Agentes'
                  },
                },
                {
                  path: ':admin_id',
                  name: 'Account agent',
                  component: () => import('@/views/account/Admin.vue'),
                  meta: {
                    resource: 'admin-view',
                    name: 'Agente'
                  },
                }
              ]
            },*/
          ]
        },
        {
          path: 'account-agents',
          name: 'Account agents',
          meta: {
            name: 'Agentes de cuenta'
          },
          redirect: { name: 'Accounts agents' },
          component: () => import('@/views/account/AdminRouter.vue'),
          children: [
            {
              path: '',
              name: 'Accounts agents',
              component: () => import('@/views/account/Admins.vue'),
              meta: {
                resource: 'admin-view',
                name: 'Agentes'
              },
            },
            {
              path: ':admin_id',
              name: 'Account agent',
              component: () => import('@/views/account/Admin.vue'),
              meta: {
                resource: 'admin-view',
                name: 'Agente'
              },
            }
          ]
        },
        {
          path: 'reports',
          name: 'Report',
          meta: {
            resource: 'report',
            name: 'Reportes'
          },
          component: () => import('@/views/account/Reports.vue'),
        },
        {
          path: 'contract',
          name: 'Contract',
          meta: {
            resource: 'contract-view',
            name: 'Contrato'
          },
          component: () => import('@/views/account/Contract.vue'),
        },
        {
          path: 'manual',
          name: 'Manual',
          meta: {
            resource: 'manual-view',
            name: 'Manual'
          },
          component: () => import('@/views/account/Manual.vue'),
        },
        {
          path: 'bug',
          name: 'Bug',
          meta: {
            resource: 'bug-view',
            name: 'Reportar error'
          },
          component: () => import('@/views/account/Bug.vue'),
        },
        {
          path: 'training',
          name: 'Training',
          meta: {
            resource: 'training-view',
            name: 'Entrenamiento'
          },
          component: () => import('@/views/account/Training.vue'),
        },
        {
          path: 'learning',
          name: 'Learning',
          meta: {
            resource: 'training-view',
            name: 'Aprendizaje'
          },
          component: () => import('@/views/account/Learning.vue'),
        },
        {
          path: 'widget',
          name: 'Widget',
          meta: {
            resource: 'bmessaging-view',
            name: 'Probar Chat'
          },
          component: () => import('@/views/account/Widget.vue'),
          // beforeEnter: async (to, from, next) => {
          //   console.log(getCurrentUser())
          //   let account = await getAccount(to.params.account_id)
          //   if(!account) {
          //     Vue.prototype.$message.error('Tipo de cuenta no valido')
          //     next({ name: 'root' })
          //   }

          //   next()
          //   // let isEcommerce = (new RegExp('^konverza-ecommerce', 'i')).test(account.type)
          //   // let isLeeds = account.type == 'konverza-leads-gallery'
          //   // if(isEcommerce || isLeeds) {
          //   //   if(account.username && account.username.length) {
          //   //     window.open(`https://${account.username}.konverza.me`, '_blank')
          //   //   }
          //   //   next({name: 'Konverza Shop', params: to.params})
          //   // } else {
          //   //   let bmessaging = await getBmessaging()
          //   //   let bmid = bmessaging && bmessaging.length ? bmessaging[0].bmid : null
          //   //   window.open(`/probar-chatbot?bmid=${bmid}&a=${to.params.account_id}`, '_blank')
          //   //   next()
          //   // }
          // },
        },
        {
          path: 'roadmap',
          name: 'Roadmap',
          meta: {
            resource: 'roadmap',
            name: 'Roadmap'
          },
          component: () => import('@/views/account/Roadmap.vue'),
          children: [
            {
              path: ':milestone_id',
              name: 'Milestone',
              component: () => import('@/components/Milestone.vue')
            }
          ]
        },
        {
          path: 'references',
          name: 'References',
          meta: {
            // resource: 'admin-view',
            name: 'Referencias'
          },
          component: () => import('./views/account/References.vue'),
        },
        {
          path: 'account-settings',
          name: 'Account Settings',
          meta: {
            resource: 'admin-view',
            name: 'Configuración de cuenta'
          },
          component: () => import('@/views/account/AccountSettings.vue'),
        },
        {
          path: 'account-cases',
          name: 'Account cases',
          meta: {
            name: 'Casos'
          },
          redirect: { name: 'Summary' },
          component: () => import('@/views/account/AccountCasesRouter.vue'),
          children: [
            {
              path: '',
              name: 'Summary',
              component: () => import('./views/account/AccountCasesSummary.vue'),
              meta: {
                resource: 'case',
                name: 'Resúmen'
              },
            },
            {
              path: 'list',
              name: 'List cases',
              component: () => import('@/views/account/AccountCases.vue'),
              meta: {
                resource: 'case',
                name: 'Lista de casos'
              },
            },
            {
              path: ':case_id',
              name: 'Account case',
              component: () => import('@/views/account/AccountCase.vue'),
              meta: {
                resource: 'case',
                name: 'Caso'
              },
            }
          ]
        },
        {
          path: 'home',
          name: 'First Steps',
          meta: {
            name: 'Inicio'
          },
          component: () => import('./views/account/EcommerceHome.vue'),
          // beforeEnter: async (to, from, next) => {
          //   if (store.getters['organization/parsedPlugins'].length) {
          //     next()
          //   } else {
          //     await store.dispatch('organization/plugins', {
          //       organization_id: store.state.account.account.organization_id,
          //       callback: (res) => {
          //         if (res.error) {
          //          console.log(res.error)
          //         }
          //         next()
          //       }
          //     })
          //   }
          // }
        },
        {
          path: 'shop',
          name: 'Konverza Shop',
          component: () => import('@/views/account/Shop.vue')
        },
        {
          path: 'suggestions',
          name: 'Suggestions',
          meta: {
            name: 'Sugerencias'
          },
          component: () => import('@/views/account/Suggestions.vue')
        },
        {
          path: 'knowledge',
          name: 'Knowledge',
          meta: {
            name: 'Conocimiento',
            resource: 'knowledge-view'
          },
          redirect: { name: 'documents' },
          component: () => import('@/views/account/Knowledge/Knowledge.vue'),
          children: [
            {
              path: 'documents',
              name: 'documents',
              component: () => import('@/views/account/Knowledge/Documents.vue'),
              meta: { name: 'Documentos' },
              children: [
                {
                  path: ':documentId',
                  name: 'document',
                  component: () => import('@/views/account/Knowledge/DocumentView.vue'),
                  meta: { name: 'Documento' },
                },
              ]
            },
            {
              path: 'links',
              name: 'links',
              component: () => import('@/views/account/Knowledge/Links.vue'),
              meta: { name: 'Enlaces' },
              children: [
                {
                  path: ':linkId',
                  name: 'link',
                  component: () => import('@/views/account/Knowledge/LinkView.vue'),
                  meta: { name: 'Enlace' },
                },
              ]
            },
          ]
        },
        {
          path: 'contacts',
          name: 'Contacts',
          component: () => import('@/views/account/Contacts.vue'),
          meta: {
            // resource: 'nerd-view',
            name: 'Contactos'
          }
        },
        {
          path: 'insights',
          name: 'Insights',
          meta: {
            resource: 'insights-view',
            name: 'Entendimiento'
          },
          component: () => import('./views/account/Insights.vue'),
        },
        {
          path: 'feedback',
          name: 'Feedback',
          meta: {
            //resource: 'feedback-view',
            //name: 'Feedback'
          },
          component: () => import('./views/account/Feedback.vue'),
        },
        {
          path: 'objectives',
          name: 'Objectives',
          meta: {
            resource: 'objectives-view',
            name: 'Objetivos'
          },
          component: () => import('./views/account/Objectives.vue'),
          children: [
            {
              path: ':objective_id',
              name: 'Objective',
              component: () => import('@/components/ObjectiveBlock.vue'),
            }
          ]
        },
        {
          path: 'graph-answers',
          name: 'Graph Answers',
          meta: {
            resource: 'graph-answer-view',
            name: 'Respuestas de grafos'
          },
          component: () => import('./views/account/GraphAnswers.vue'),
          children: [

          ]
        },
        {
          path: 'activity-logs',
          name: 'Activity Logs',
          meta: {
            resource: 'activity-logs-view',
            name: 'Actividad'
          },
          component: () => import('./views/account/ActivityLogs'),
        },
        {
          path: 'webhooks',
          name: 'Webhooks',
          meta: {
            resource: 'webhooks-view',
            name: 'Webhooks'
          },
          component: () => import('./views/account/Webhooks')
        },
        {
          path: 'api',
          name: 'API',
          meta: {
            resource: 'api-view',
            name: 'API'
          },
          component: () => import('./views/account/ApiUsers')
        },
        {
          path: 'subscription',
          name: 'Subscription',
          meta: {
            resource: 'billing-view',
            name: 'Suscripción'
          },
          redirect: { name: 'Bills' },
          component: () => import('./views/account/AccountBilling/BillingRouter.vue'),
          children: [
            {
              path: 'bills',
              name: 'Bills',
              meta: { name: 'Facturas' },
              component: () => import('./views/account/AccountBilling/BillList.vue'),
            }
          ]
        },
        /*
        {
          path: 'functions',
          name:'Functions',
          meta: {
            resource: 'functions-view',
            name: 'Functions'
          },
          component: () => import('./views/account/Functions')
        },
        */
        // more views to come
      ]
    },
    // CERTIFICATION VIEWS
    {
      path: '/learn',
      meta: {
        requiresAuth: true
      },
      component: () => import('./views/common/simpleRouter.vue'),
      children: [
        {
          path: '',
          name: 'Learn',
          component: () => import('./views/main/Learn.vue')
        },
        {
          path: 'videos',
          name: 'Learn Videos',
          component: () => import('./views/main/LearnVideos.vue')
        }
      ]
    },
    {
      path: '/*',
      redirect: { name: 'NotFound' }
    }
  ]
})

var hasRulesInStorage = function () {
  let currentTime = (new Date()).getTime()

  let rulesExists = Boolean(getItem('rules') && Array.isArray(getItem('rules')) && getItem('rules').length)
  let needsUpdate = Math.round((currentTime - (getItem('rulesUpdatedTime') || 0)) / 1000) > 60 * 15

  return rulesExists && !needsUpdate
}

var getCurrentUser = function () {
  let user = Vue.prototype.$getCookie('user')
  if (user) {
    return user
  } else {
    return {}
  }
}

var getEcommerce = function (account_id) {
  return new Promise(async (resolve, reject) => {
    try {
      let res = await Vue.prototype.$request.get(`account/${account_id}/ecommerce`)
      if (res.data.success && res.data.resource.delivery_type_id) {
        resolve(res.data.resource.delivery_type_id)
      } else {
        let res_update = await Vue.prototype.$request.patch(`account/${account_id}/ecommerce`, {
          account_id: account_id,
          delivery_enabled: true,
          pickup_enabled: true,
          delivery_type_id: 1,
        })
        if (res_update.data.success) {
          resolve(1)
        } else {
          resolve(null)
        }
      }
    } catch (e) {
      console.error(e)
      resolve(null)
    }
  })
}

var getContract = function (partner_id) {
  return new Promise(async (resolve, reject) => {
    try {
      let res = await Vue.prototype.$request.get(`partner/${partner_id}`)
      if (res.data.success) {
        resolve(!!res.data.resource.contract_acknowledge)
      } else {
        resolve(null)
      }
    } catch (e) {
      console.error(e)
      resolve(null)
    }
  })
}

const getAccount = async (accountId) => {
  try {
    const accounts = store.getters['account/loadAccounts'] || []
    const accountFound = accounts.find(account => account.account_id === accountId)

    if (accountFound) return accountFound

    const res = await Vue.prototype.$request.get(`account/${accountId}`)
    const { resource } = res.data || {}

    return resource
  } catch (e) {
    console.error(e)
  }
}

var getBmessaging = function (account_id) {
  return new Promise(async (resolve, reject) => {
    try {
      let res = await Vue.prototype.$request.get(`account/${account_id}/bmessaging`)
      if (res.data.success && res.data.resource) {
        resolve(res.data.resource)
      } else {
        resolve(null)
      }
    } catch (e) {
      console.error(e)
      resolve(null)
    }
  })
}

// verify data before route
let selfGuard = async (to, from, next) => {
  try {
    document.title = getDashboardTitle(getDashboardType());
  } catch (e) {}

  if (to.matched.some((record) => record.meta.requiresAuth)) {
    if (!auth.getters.isAuthenticated()) {
      try {
        const token = await getRefreshToken()
        if(!token) throw new Error('No token')
      } catch (error) {
        console.error(error)
        store.commit('auth/setRedirectUrl', to.fullPath)
        return next({ name: "logout" })
      }
      
    }

    if (!ability.rules.length) {
      if (hasRulesInStorage()) {
        store.commit("auth/updatePermissions", getItem("rules"))
      } else {
        try {
          await store.dispatch("auth/role")
        } catch (e) {
          Vue.prototype.$message.error("Ocurrió un error validando la sesión.")
          store.commit('auth/setRedirectUrl', to.fullPath)
          return next({name: "logout" })
        }
      }
    }
  }

  if (to.meta.resource) {
    try {
      if (!ability.can("read", to.meta.resource)) {
        console.error("not authorized");
        Vue.prototype.$message.error("No tienes acceso a esta sección");
        if (!to.params) return next({ name: "NoAccess" });
        const { account_id } = to.params;
        if (account_id)
          return next({ name: "PluginsLoader", params: to.params });
        return next({ name: "root" });
      }
    } catch (e) {
      console.error(e.message);
      return false;
    }
  }

  return next()
}

//router.beforeEach(process.env.VUE_APP_ENV_MODE == 'development' ? selfGuard : authGuard)
router.beforeEach(selfGuard)

export default router

// {
//   path: '/about',
//   name: 'about',
//   // route level code-splitting
//   // this generates a separate chunk (about.[hash].js) for this route
//   // which is lazy-loaded when the route is visited.
//   component: () => import(/* webpackChunkName: "about" */ './views/About.vue')
// }
